import { z } from "zod";

import { DiscountRoomConditionType, SaleTypeForTerm } from "@/lib/gql/__generated__/graphql";
import {
	DiscountLongTermInputSchema,
	LongTermStayDiscountObjectInputSchema,
} from "@/lib/zod/__generated__/validation-schema";

import {
	chillnnDateZodSchema,
	priceObjectZodSchema,
} from "@/components/features/_shared/validation-schema/validation-schema";

import * as DiscountItemShared from "../../../../_shared";

const ERROR_MESSAGES = {
	validIncludeSmallestNights: {
		must: `宿泊日数は1泊以上を入力してください`,
		custom: (latestSmallestNights: number) => `宿泊日数は${latestSmallestNights}泊目以降を入力してください`,
	},
	validDiscountAmount: {
		ratioMin: "割引率は0%以上を入力してください",
		ratioMax: "割引率は100%以下を入力してください",
	},
};

const LongTermStayDiscountObjectSchema = LongTermStayDiscountObjectInputSchema.extend({
	includeSmallestNights: z.number().min(1, ERROR_MESSAGES.validIncludeSmallestNights.must),
	ratio: z
		.number()
		.min(0, { message: ERROR_MESSAGES.validDiscountAmount.ratioMin })
		.max(100, { message: ERROR_MESSAGES.validDiscountAmount.ratioMax })
		.nullish(),
	static: priceObjectZodSchema.nullish(),
});

const LongTermStayDiscountObjectArraySchema = z.object({
	longTermStayDiscountObject: z.array(LongTermStayDiscountObjectSchema).superRefine((value, ctx) => {
		value.forEach((current, index) => {
			/**
			 * first  はスキップする
			 */
			if (index === 0) return z.NEVER;

			const prevIncludeSmallestNights = value[index - 1]?.includeSmallestNights;
			const currentIncludeSmallestNights = current.includeSmallestNights;
			/**
			 * current が大きればスキップする
			 */
			if (prevIncludeSmallestNights < currentIncludeSmallestNights) return z.NEVER;

			/**
			 * prev が current 以上であればバリデーションを起こす
			 * @example
			 * ```
			 * prev: 3, current: 4 -> OK
			 * prev: 3, current: 3 -> ERROR
			 * prev: 3, current: 2 -> ERROR
			 * ```
			 */
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: ERROR_MESSAGES.validIncludeSmallestNights.custom(prevIncludeSmallestNights),
				path: [index, "includeSmallestNights"],
			});
		});
	}),
});

/**
 * 連泊ディスカウントの設定用のバリデーションスキーマ
 */
export const DiscountLongTermInputZodSchema = DiscountLongTermInputSchema.extend({
	saleStart: chillnnDateZodSchema.nullish().or(z.literal("")),
	saleEnd: chillnnDateZodSchema.nullish().or(z.literal("")),

	stayableStart: chillnnDateZodSchema.nullish().or(z.literal("")),
	stayableEnd: chillnnDateZodSchema.nullish().or(z.literal("")),

	// 部屋の適用条件
	roomConditionType: z.nativeEnum(DiscountRoomConditionType),
})
	.merge(LongTermStayDiscountObjectArraySchema)
	.merge(DiscountItemShared.Model.ValidationSchema.CommonDiscountInputZodSchema)
	// 公開する期間（設定する場合は開始日・終了日をセットで設定する）
	.refine(
		({ saleStart, saleEnd, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				return !!saleStart === !!saleEnd;
			}

			return true;
		},
		{
			message: "公開する期間は開始日と終了日の両方を設定してください",
			path: ["saleEnd"],
		},
	)
	// 公開する期間
	.refine(
		({ saleStart, saleEnd, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				if (!!saleStart && !!saleEnd) {
					return saleStart <= saleEnd;
				}

				return true;
			}

			return true;
		},
		{
			message: "終了日が開始日以降になるように設定してください",
			path: ["saleEnd"],
		},
	)
	// 滞在可能な期間
	.refine(
		({ stayableStart, stayableEnd, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				if (!!stayableStart && !!stayableEnd) {
					return stayableStart <= stayableEnd;
				}

				// 片側だけ設定して保存するとサーバー側の適用判定で割引が一切適用されなくなるため、両方必須にする
				return false;
			}

			return true;
		},
		{
			message: "滞在可能な期間を設定してください",
			path: ["stayableEnd"],
		},
	);

export type DiscountLongTermInputZodSchemaType = z.infer<typeof DiscountLongTermInputZodSchema>;
